/**
 * @prop --text-align: Text align of field
 * @prop --input-width: Width of of field
 * @prop --padding-top: Padding top of field
 * @prop --padding-bottom: Padding bottom of field
 * @prop --padding-left: Padding left of field
 * @prop --padding-right: Padding right of field
 *
 * @prop --stzh-form-input-height: **Global**: Height of fields & buttons
 * @prop --stzh-form-input-small-height: **Global**: Height of fields & buttons when small variant is used
 * @prop --stzh-form-input-tiny-height: **Global**: Height of fields & buttons when tiny variant is used
 */

:host {
  --text-align: left;
  --color: var(--stzh-input-color, #{$formColor});
  --border-color: #{$formBorderColor};
  --border-radius: #{$formInputBorderRadius};
  --background-color: #{$formBackgroundColor};
  --padding-top: #{space("xsmall")};
  --padding-bottom: #{space("xsmall")};
  --padding-left: #{space("medium")};
  --padding-right: #{space("medium")};
  --height: #{$formInputHeight};
  --input-width: 100%;
  --label-color: var(--stzh-input-label-color, #{$formLabelColor});
  --placeholder-color: #{$formInputPlaceholderColor};
  --button-right-color: #{$colorGrey80};

  --hover-color: var(--stzh-input-hover-color, #{$formHoverColor});
  --hover-border-color: var(
    --stzh-input-hover-border-color,
    #{$formHoverBorderColor}
  );
  --hover-background-color: #{$formHoverBackgroundColor};
  --hover-button-right-color: #{$colorPrimary70};

  --focus-color: #{$formFocusColor};
  --focus-border-color: #{$formFocusBorderColor};
  --focus-background-color: #{$formFocusBackgroundColor};
  --focus-button-right-color: #{$colorPrimary70};

  --text-selection-background-color: #{$colorSecondary30};

  &[type="hidden"] {
    position: absolute;
    top: -9999px;
    left: -9999px;
    visibility: hidden;
  }

  width: 100%;

  &[size="small"] {
    --height: #{$formInputHeightSmall};
  }

  /* Filled */

  &[is-filled]:not([is-filled="false"]) {
    --button-right-color: #{$colorPrimary70};
    --hover-button-right-color: #{$colorPrimary70};
    --focus-button-right-color: #{$formFocusColor};
  }

  /* Invalid */

  &[is-invalid] {
    --color: #{$colorError60};
    --border-color: #{$colorError60};
    --background-color: #{$colorWhite};
    --label-color: #{$colorError60};
    --placeholder-color: #{$colorError60};
    --button-right-color: #{$colorError60};

    --hover-color: #{$colorError60};
    --hover-border-color: #{$colorError60};
    --hover-background-color: #{$colorWhite};
    --hover-button-right-color: #{$colorError60};

    --focus-color: #{$colorError60};
    --focus-border-color: #{$colorError60};
    --focus-background-color: #{$colorWhite};
    --focus-button-right-color: #{$colorError60};
  }

  &[readonly]:not([readonly="false"]),
  &[disabled]:not([disabled="false"]) {
    --color: #{$formDisabledColor};
    --border-color: #{$formDisabledBorderColor};
    --background-color: #{$formDisabledBackgroundColor};
    --label-color: #{$formDisabledColor};
    --placeholder-color: #{$formDisabledColor};
    --description-color: #{$formDisabledColor};
    --button-right-color: #{$colorGrey70};

    --hover-color: #{$formDisabledColor};
    --hover-border-color: #{$formDisabledBorderColor};
    --hover-background-color: #{$formDisabledBackgroundColor};
    --hover-button-right-color: #{$colorGrey70};

    --focus-color: #{$formDisabledColor};
    --focus-border-color: #{$formDisabledBorderColor};
    --focus-background-color: #{$formDisabledBackgroundColor};
    --focus-button-right-color: #{$colorGrey70};
  }

  /* Internal (Hover / Focus) */

  &:is(:has(.stzh-input__input:hover)) {
    --button-right-color: var(--hover-button-right-color);
  }

  &:is(:has(.stzh-input__input:focus)) {
    --button-right-color: var(--focus-button-right-color);
  }
}

:host ::slotted([slot="button-right"]),
.stzh-input__button-right {
  position: absolute;
  top: 0px;
  right: 0px;
}

:host ::slotted(stzh-popover[slot="button-right"]),
:host ::slotted([slot="button-right"]) stzh-popover {
  display: block;
}

:host ::slotted(stzh-button[slot="button-right"]),
:host
  ::slotted(stzh-button[slot="button-right"][disabled]:not([disabled="false"])),
:host
  ::slotted(stzh-popover[slot="button-right"])
  .stzh-popover__trigger
  stzh-button,
:host
  ::slotted(stzh-popover[slot="button-right"])
  .stzh-popover__trigger
  stzh-button[disabled]:not([disabled="false"]),
:host
  ::slotted(stzh-tooltip[slot="button-right"])
  .stzh-tooltip__trigger
  stzh-button,
:host
  ::slotted(stzh-tooltip[slot="button-right"])
  .stzh-tooltip__trigger
  stzh-button[disabled]:not([disabled="false"]),
.stzh-input__button-right,
.stzh-input__button-right[disabled]:not([disabled="false"]) {
  --color: var(--button-right-color);
  --background-color: transparent;
  --border-color: transparent;

  --hover-color: var(--button-right-color);
  --hover-background-color: transparent;
  --hover-border-color: transparent;
}

// :host ::slotted(stzh-button[slot="button-right"][disabled]:not([disabled="false"])),
// :host ::slotted([slot="button-right"]) stzh-button[disabled]:not([disabled="false"]),
// .stzh-input__button-right[disabled]:not([disabled="false"]) {
//   --hover-color: #{$colorGrey80};
// }

:host
  ::slotted(
    stzh-button[slot="button-right"][size="default"][icon-only]:not(
        [icon-only="false"]
      )
  ),
:host
  ::slotted(stzh-popover[slot="button-right"])
  .stzh-popover__trigger
  stzh-button[icon-only][size="default"]:not([icon-only="false"]),
:host
  ::slotted(stzh-tooltip[slot="button-right"])
  .stzh-tooltip__trigger
  stzh-button[icon-only][size="default"]:not([icon-only="false"]),
.stzh-input__button-right[size="default"][icon-only]:not([icon-only="false"]) {
  --icon-size: #{iconSize("medium")};
}

.stzh-input {
  @include input-description;

  ::selection {
    background: var(--text-selection-background-color);
  }

  &__field-wrapper {
    position: relative;
    display: flex;
    width: var(--input-width);
  }

  &__input {
    @include font;
    @include fontSize("milli");
    border: 2px solid var(--border-color);
    transition-property: color, border-color, background-color;
    transition-duration: $baseTransitionAnimationSpeed;
    border-radius: var(--border-radius);
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
    padding-left: var(--padding-left);
    padding-right: var(--padding-right);
    margin: 0;
    width: 100%;
    color: var(--color);
    background-color: var(--background-color);
    appearance: none;
    text-align: var(--text-align);

    @include placeholder {
      color: var(--placeholder-color);
    }

    &:hover {
      color: var(--hover-color);
      border-color: var(--hover-border-color);
      background-color: var(--hover-background-color);
    }

    &:focus {
      color: var(--focus-color);
      border-color: var(--focus-border-color);
      background-color: var(--focus-background-color);
    }

    // prevent ugly autofill background color in chrome
    &:-webkit-autofill {
      background-clip: text;
    }

    // show ellipsis for placeholders that are too long
    &:placeholder-shown {
      text-overflow: ellipsis;
    }

    // hide search clear icon on chrome
    &[type="search"]::-webkit-search-decoration,
    &[type="search"]::-webkit-search-cancel-button,
    &[type="search"]::-webkit-search-results-button,
    &[type="search"]::-webkit-search-results-decoration {
      -webkit-appearance: none;
    }

    /* Normal inputs */

    &--input {
      height: var(--height);
    }

    /* Textarea */

    &--textarea {
      min-height: var(--height);
      resize: vertical;
    }
  }

  &__label {
    @include font("heavy");
    @include fontSize("micro");
    @include truncate;
    display: block;
    color: var(--label-color);
    width: auto;
    max-width: 100%;
    margin-bottom: space("xxxsmall");
    // position: absolute;
    // top: calc(#{$formInputHeight} / 2);
    // // padding-left (medium) + border-left
    // left: calc(#{space('medium')} + 1px);
    // max-width: calc(100% - #{space('medium')} * 2);
    // transform: translateY(-50%);
    // transition: all $baseTransitionAnimationSpeed;
    // pointer-events: none;

    &.no-label-truncate {
      @include resetTruncate;
    }

    &:empty {
      display: none;
    }

    // &::before {
    //   content: '';
    //   display: none;
    //   background: $colorWhite;
    //   position: absolute;
    //   width: 100%;
    //   z-index: -1;
    //   left: 0;
    //   top: 50%;
    //   // prevent white line not fully overlaping border of input
    //   margin-top: -0.5px;
    //   height: 2px;
    // }
  }

  &__marker {
    @include fontSize("nano");
    margin-left: calc(-0.25em + #{space("xsmall")});
  }

  /* Clearable / Search input / Password  */

  &--type-password#{&}--is-not-filled &__button-right,
  &--type-password#{&}--is-readonly &__button-right,
  &--type-password#{&}--is-disabled &__button-right,
  &--type-search#{&}--is-not-clearable &__button-right,
  &--type-search#{&}--is-not-filled &__button-right,
  &--type-search#{&}--is-readonly &__button-right,
  &--type-search#{&}--is-disabled &__button-right {
    pointer-events: none;
  }

  /* Styles for floating label */

  // &:not(#{&}--is-readonly):not(#{&}--is-disabled) &__input:focus + &__label,
  // &--has-fixed-label &__label,
  // &--is-filled &__label,
  // &--is-readonly &__label,
  // &--is-disabled &__label,
  // &--is-invalid &__label {
  //   display: block;
  //   top: 0;
  //   // padding-left (input field medium) - padding-left
  //   left: calc(#{space('medium')} - #{space('xxsmall')});
  //   height: 20px; // dividable by two (so before element is always positioned correctly)
  //   line-height: 20px;
  //   padding: 0 space('xxsmall');
  //   max-width: calc(100% - #{space('small')} * 2);
  //   pointer-events: all;

  //   &:not(:empty)::before {
  //     display: block;
  //   }
  // }

  /* Button Right Variant */

  &--has-button-right &__input {
    // distance to icon + icon button width
    padding-right: var(--height);
  }

  // &--has-button-right &__label {
  //   // 100% - ((padding left of normal variant + border 1px) * 2) - icon button width
  //   max-width: calc(100% - (#{space('medium')} + 1px) * 2 - #{$formInputHeight});
  // }

  // &--has-button-right#{&}:not(#{&}--is-readonly):not(#{&}--is-disabled) &__input:focus + &__label,
  // &--has-button-right#{&}--has-fixed-label &__label,
  // &--has-button-right#{&}--is-filled &__label,
  // &--has-button-right#{&}--is-readonly &__label,
  // &--has-button-right#{&}--is-disabled &__label,
  // &--has-button-right#{&}--is-invalid &__label {
  //   // 100% - left distance to text + 1px border + padding label right side - icon button width
  //   max-width: calc(100% - #{space('medium')} - #{space('xxsmall')} - 1px + #{space('xxsmall')} - #{$formInputHeight});
  // }

  /* Size variants */

  &--size-small &__input {
    @include fontSize("micro");
  }

  // &--size-small &__label {
  //   top: calc(#{$formInputHeightSmall} / 2);
  // }

  // &--size-small#{&}:not(#{&}--is-readonly):not(#{&}--is-disabled) &__input:focus + &__label,
  // &--size-small#{&}--has-fixed-label &__label,
  // &--size-small#{&}--is-filled &__label,
  // &--size-small#{&}--is-readonly &__label,
  // &--size-small#{&}--is-disabled &__label,
  // &--size-small#{&}--is-invalid &__label {
  //   top: 0;
  // }

  // &--size-small#{&}--has-button-right &__label {
  //   max-width: calc(100% - (#{space('medium')} + 1px) * 2 - #{$formInputHeightSmall});
  // }

  // &--size-small#{&}--has-button-right#{&}:not(#{&}--is-readonly):not(#{&}--is-disabled) &__input:focus + &__label,
  // &--size-small#{&}--has-button-right#{&}--has-fixed-label &__label,
  // &--size-small#{&}--has-button-right#{&}--is-filled &__label,
  // &--size-small#{&}--has-button-right#{&}--is-readonly &__label,
  // &--size-small#{&}--has-button-right#{&}--is-disabled &__label,
  // &--size-small#{&}--has-button-right#{&}--is-invalid &__label {
  //   max-width: calc(100% - #{space('medium')} - #{space('xxsmall')} - 1px + #{space('xsmall')} - #{$formInputHeightSmall});
  // }

  /* Spin button */

  &:not(#{&}--has-spin) &__input {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      appearance: none;
      margin: 0;
    }

    &[type="number"] {
      appearance: textfield;
    }
  }

  /* Hidden label */

  &--has-label-hidden &__label {
    @include visuallyhidden;
  }

  /* Readonly / Disabled */

  &--is-readonly &__input,
  &--is-disabled &__input,
  &--is-readonly &__label,
  &--is-disabled &__label {
    cursor: not-allowed;
  }

  // &--is-readonly &__label,
  // &--is-disabled &__label {
  //   &::before {
  //     background: transparent;
  //   }
  // }
}
